/**
 * What a pail is pointed at.
 *
 * A rule of its own, rather than a predicate chosen at the call site, because
 * it is the opposite of the rule everything else aims by and the two look
 * almost identical when read quickly.
 *
 * **Everything else aims through water.** The ordinary ray treats a fluid as
 * empty, on purpose: it is what lets a spade reach the clay on a lake bed and
 * a pick reach a drowned seam. Pointing a pail with that ray means the ray
 * flies over the lake and stops at whatever solid thing is behind it - usually
 * nothing within reach - so the pail comes up dry while the player is staring
 * straight at a sea.
 *
 * Measured before this existed: from ninety-eight places along a shore,
 * looking down at the water, the ordinary ray found something to scoop at two
 * of them. This one finds it at eighty-four.
 */

import { raycastVoxels } from "../physics/raycast.js";
import { BlockId, isWaterSource, OIL } from "../block/registry.js";

/** The four cells beside one, on the flat. */
const BESIDE: readonly (readonly [number, number])[] = [
  [1, 0], [-1, 0], [0, 1], [0, -1],
];

export interface Cell { x: number; y: number; z: number; }

/**
 * The water source a pail would fill from, or null if it is aimed at anything
 * else - a wall, a stream, or the open sky.
 *
 * A **source** only. Scooping from a trickle would turn one bucket into an
 * unlimited supply: pour it out, let it run its eight blocks, and fill again
 * from the far end of its own flow.
 */
export function waterToScoop(
  getBlock: (x: number, y: number, z: number) => number,
  eye: { x: number; y: number; z: number },
  look: { x: number; y: number; z: number },
  reach: number,
): Cell | null {
  const hit = raycastVoxels(
    eye.x, eye.y, eye.z, look.x, look.y, look.z, reach,
    // Stops at anything at all, water included. That single difference from
    // the aiming ray is the whole of this file.
    (x, y, z) => getBlock(x, y, z) !== BlockId.Air,
  );
  if (hit === null) return null;
  if (!isWaterSource(getBlock(hit.x, hit.y, hit.z))) return null;
  return { x: hit.x, y: hit.y, z: hit.z };
}

/**
 * And the нафта a pail would fill from, on the same terms.
 *
 * A pool only, never a trickle, for the reason above: a bucket that can be
 * filled from the far end of its own spill is an unlimited supply of the one
 * thing a танк is made of.
 */
export function oilToScoop(
  getBlock: (x: number, y: number, z: number) => number,
  eye: { x: number; y: number; z: number },
  look: { x: number; y: number; z: number },
  reach: number,
): Cell | null {
  const hit = raycastVoxels(
    eye.x, eye.y, eye.z, look.x, look.y, look.z, reach,
    (x, y, z) => getBlock(x, y, z) !== BlockId.Air,
  );
  if (hit === null) return null;
  if (getBlock(hit.x, hit.y, hit.z) !== OIL) return null;
  return { x: hit.x, y: hit.y, z: hit.z };
}

/**
 * Would the water close over a cell you took out of it?
 *
 * Two sources beside it and it would: that is a lake, and a lake does not
 * miss one bucketful. One or none and it would not: that is a puddle
 * somebody poured, and taking it is the only way to get it back.
 *
 * This is the rule that was missing. Filling a pail never took the cell at
 * all - right for the lake it was written for, and it meant a bucketful
 * poured onto a field could never be picked up again and could be scooped
 * from for ever: "створюється безкінечне джерело води".
 *
 * Two, rather than one, because one would let a lake be drained a cell at a
 * time from its edge inwards; and it is the same two that makes a pair of
 * poured sources into a well you can keep drawing from, which is a thing
 * players do on purpose.
 */
export function poolClosesOver(
  getBlock: (x: number, y: number, z: number) => number,
  cell: Cell,
): boolean {
  let sources = 0;
  for (const [dx, dz] of BESIDE) {
    if (isWaterSource(getBlock(cell.x + dx, cell.y, cell.z + dz))) sources++;
  }
  return sources >= 2;
}
